/**
 * Pure graph model, layout and visual scales for {@link FlowCanvas}.
 *
 * Kept separate from the component so the canvas file exports components only
 * (fast refresh) and so the layout maths stays trivially testable.
 */

// ─── Graph model ──────────────────────────────────────────────────────────────

export type NodeKind = 'topic' | 'connector' | 'store' | 'placeholder'

export interface FlowNodeSpec {
  id: string
  label: string
  /** Column index — left-to-right stage. Gaps collapse; ties stack vertically. */
  col: number
  kind?: NodeKind
  /** 'healthy' | 'lagging' | 'stalled' | 'unknown' | 'RUNNING' | 'FAILED' | … */
  status?: string
  /** Messages per minute; -1 or undefined means unknown. */
  ratePerMin?: number
  lag?: number
  /** Seconds to drain lag at current rate; counts down live between refreshes. */
  etaSec?: number | null
  errorTrace?: string | null
  sublabel?: string
  onClick?: () => void
}

export interface FlowEdgeSpec {
  from: string
  to: string
  /** Defaults to the target node's rate. */
  ratePerMin?: number
  /** Defaults to the target node's status. */
  status?: string
  label?: string
}

// ─── Layout constants ─────────────────────────────────────────────────────────

export const NODE_W = 184
/**
 * Tall enough for a node's fullest form: label + status chip, sublabel, the
 * rate line, and then either the lag line or "caught up". Those four rows plus
 * padding and borders measure 79px, so this leaves a little slack — the card
 * clips (overflow-hidden), and at 66 the last line was silently truncated.
 * Raise this if a fifth row is ever added to FlowNode.
 */
export const NODE_H = 84
const COL_GAP = 88
const ROW_GAP = 16
const PAD_X = 6
const PAD_Y = 10

// ─── Status tones ─────────────────────────────────────────────────────────────

export type Tone = 'ok' | 'warn' | 'bad' | 'idle'

export function toneOf(status?: string): Tone {
  switch (status) {
    case 'healthy':
    case 'RUNNING':
      return 'ok'
    case 'lagging':
    case 'PAUSED':
      return 'warn'
    case 'stalled':
    case 'FAILED':
    case 'UNASSIGNED':
      return 'bad'
    default:
      return 'idle'
  }
}

export const TONE_HEX: Record<Tone, string> = {
  ok: '#22c55e',
  warn: '#f59e0b',
  bad: '#ef4444',
  idle: '#94a3b8',
}

export const TONE_CARD: Record<Tone, string> = {
  ok: 'border-green-200 bg-green-50/60',
  warn: 'border-amber-200 bg-amber-50/70',
  bad: 'border-red-300 bg-red-50',
  idle: 'border-gray-200 bg-white',
}

export const TONE_CHIP: Record<Tone, string> = {
  ok: 'bg-green-100 text-green-700',
  warn: 'bg-amber-100 text-amber-700',
  bad: 'bg-red-100 text-red-700',
  idle: 'bg-gray-100 text-gray-500',
}

// ─── Visual scales ────────────────────────────────────────────────────────────

/** Log-scaled stroke width so 60/m and 60k/m are both readable on one canvas. */
export function edgeWidth(ratePerMin?: number): number {
  if (ratePerMin == null || ratePerMin <= 0) return 1.5
  return Math.min(7, 1.4 + Math.log10(ratePerMin + 1) * 1.5)
}

/** Particle count and travel time — more and faster as throughput climbs. */
export function particleSpec(ratePerMin?: number): { count: number; dur: number } {
  if (ratePerMin == null || ratePerMin <= 0) return { count: 0, dur: 0 }
  const l = Math.log10(ratePerMin + 1)
  return {
    count: Math.min(6, Math.max(1, Math.round(l * 1.6))),
    dur: Math.max(1.1, 5.2 - l * 0.85),
  }
}

// ─── Formatters ───────────────────────────────────────────────────────────────

export function fmtNum(n: number): string {
  if (n >= 10000) return `${(n / 1000).toFixed(0)}k`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  if (n >= 10) return `${Math.round(n)}`
  if (n >= 1) return n.toFixed(1)
  return n < 0.005 ? '0' : n.toFixed(2)
}

export function fmtEta(sec: number | null | undefined): string {
  if (sec == null) return '?'
  if (sec === 0) return 'drained'
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  if (sec < 3600) return s > 0 ? `${m}m ${s}s` : `${m}m`
  return `${(sec / 3600).toFixed(1)}h`
}

// ─── Layout ───────────────────────────────────────────────────────────────────

export interface Placed {
  node: FlowNodeSpec
  x: number
  y: number
}

export interface Layout {
  placed: Placed[]
  pos: Map<string, Placed>
  width: number
  height: number
}

/** Columnar layout: nodes stack within their column, columns centre vertically. */
export function layoutGraph(nodes: FlowNodeSpec[]): Layout {
  const byCol = new Map<number, FlowNodeSpec[]>()
  for (const n of nodes) {
    const bucket = byCol.get(n.col)
    if (bucket) bucket.push(n)
    else byCol.set(n.col, [n])
  }

  const colKeys = [...byCol.keys()].sort((a, b) => a - b)
  const colHeights = colKeys.map(k => {
    const c = byCol.get(k)!.length
    return c * NODE_H + (c - 1) * ROW_GAP
  })
  const maxH = Math.max(NODE_H, ...colHeights)

  const placed: Placed[] = []
  colKeys.forEach((k, ci) => {
    const startY = PAD_Y + (maxH - colHeights[ci]) / 2
    byCol.get(k)!.forEach((n, ri) => {
      placed.push({
        node: n,
        x: PAD_X + ci * (NODE_W + COL_GAP),
        y: startY + ri * (NODE_H + ROW_GAP),
      })
    })
  })

  return {
    placed,
    pos: new Map(placed.map(p => [p.node.id, p])),
    width: PAD_X * 2 + colKeys.length * NODE_W + Math.max(0, colKeys.length - 1) * COL_GAP,
    height: PAD_Y * 2 + maxH,
  }
}

/** Cubic bezier from the right edge of `s` to the left edge of `t`. */
export function edgePath(s: Placed, t: Placed): string {
  const sx = s.x + NODE_W
  const sy = s.y + NODE_H / 2
  const tx = t.x
  const ty = t.y + NODE_H / 2
  const dx = Math.max(32, (tx - sx) * 0.45)
  return `M ${sx},${sy} C ${sx + dx},${sy} ${tx - dx},${ty} ${tx},${ty}`
}

/** Fan every node in `from` into every node in `to`. */
export function fanOut(from: FlowNodeSpec[], to: FlowNodeSpec[]): FlowEdgeSpec[] {
  return from.flatMap(f => to.map(t => ({ from: f.id, to: t.id })))
}
